import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RestHelperNoAuth } from "../helper/restHelperNoAuth";

const PORTAL_HOME = 'https://www.pinpointfieldservices.com';
const MAX_REPLY = 2000;
const CHAR_COUNT_THRESHOLD = 200; // show the counter only within this many chars of the limit

// Map the role returned by message-token-validate to the portal deep link.
// notary -> /notary ; escrow/title/originator/creator -> /originator ; else generic home.
function portalUrl(role) {
    if (role === 'notary') return PORTAL_HOME + '/notary';
    if (['escrow', 'title', 'originator', 'creator'].includes(role)) return PORTAL_HOME + '/originator';
    return PORTAL_HOME;
}

// Format a Unix epoch (seconds) as "May 30, 2023 at 3:29 PM".
function formatTimestamp(epochSeconds) {
    if (!epochSeconds) return '';
    const date = new Date(epochSeconds * 1000);
    const datePart = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} at ${timePart}`;
}

const MessageReply = () => {
    const { token } = useParams();
    const api = RestHelperNoAuth();

    // 'loading' | 'invalid' | 'message' | 'confirmation'
    const [pageState, setPageState] = useState('loading');
    const [message, setMessage] = useState(null);
    const [role, setRole] = useState(null);
    const [reply, setReply] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [replyError, setReplyError] = useState('');

    useEffect(() => {
        let active = true;
        api
            .postWithStatus('/message-token-validate', { token })
            .then(res => {
                if (!active) return;
                if (res.status === 200 && res.data && res.data.valid === true) {
                    setMessage(res.data.message || {});
                    setRole(res.data.role || null);
                    setPageState('message');
                } else {
                    // valid:false, non-200, or network error all show the same state.
                    setPageState('invalid');
                }
            })
            .catch(() => {
                if (active) setPageState('invalid');
            });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const onReplyChange = (evt) => {
        setReply(evt.target.value);
        if (replyError) setReplyError('');
    };

    const sendReply = () => {
        if (!reply.trim() || isSending) return;
        setIsSending(true);
        setReplyError('');
        api
            .postWithStatus('/message-token-reply', { token, reply_body: reply })
            .then(res => {
                setIsSending(false);
                if (res.status === 200 && res.data && res.data.success === true) {
                    setPageState('confirmation');
                } else {
                    // 403 (invalid/expired/used), 500, or network failure — keep the textarea.
                    setReplyError('Something went wrong. Please try again or log in to reply.');
                }
            })
            .catch(() => {
                setIsSending(false);
                setReplyError('Something went wrong. Please try again or log in to reply.');
            });
    };

    const remaining = MAX_REPLY - reply.length;

    if (pageState === 'loading') {
        return (
            <div className="flex justify-center mt-12 mb-12">
                <div className="spinner-icon" role="status">
                    <span className="sr-only">Loading</span>
                </div>
            </div>
        );
    }

    if (pageState === 'invalid') {
        return (
            <div className="max-w-xl mx-auto px-4 mt-12 mb-12 text-center">
                <h1 className="text-2xl font-semibold text-neutral-800 mb-3">This link is no longer valid.</h1>
                <p className="text-neutral-600 mb-6">Log in to access the Message Center.</p>
                <a
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-5 rounded-lg transition-colors no-underline"
                    href={PORTAL_HOME}
                >
                    Log In
                </a>
            </div>
        );
    }

    if (pageState === 'confirmation') {
        return (
            <div className="max-w-xl mx-auto px-4 mt-12 mb-12 text-center">
                <h1 className="text-2xl font-semibold text-neutral-800 mb-3">Your reply has been sent.</h1>
                <p className="text-neutral-600 mb-6">The team will follow up with you shortly.</p>
                <a className="text-blue-600 hover:text-blue-700 font-medium" href={portalUrl(role)}>
                    View full Message Center →
                </a>
            </div>
        );
    }

    // pageState === 'message'
    return (
        <div className="max-w-xl mx-auto px-4 mt-6 mb-12">
            <div className="mb-4">
                <h1 className="text-lg font-semibold text-neutral-800">Message Center</h1>
                <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
            </div>

            <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-4 mb-6">
                <p className="font-medium text-neutral-800 mb-1">
                    New message from {message && message.sender}
                </p>
                <p className="text-sm text-neutral-500 mb-3">
                    {message && formatTimestamp(message.timestamp)}
                </p>
                <p className="text-neutral-700 whitespace-pre-line">{message && message.body}</p>
            </div>

            <div>
                <label htmlFor="reply" className="block text-sm font-medium text-neutral-700 mb-2">Your Reply</label>
                <textarea
                    id="reply"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    rows="4"
                    maxLength={MAX_REPLY}
                    value={reply}
                    onChange={onReplyChange}
                    placeholder="Type your reply here..."
                />
                {remaining <= CHAR_COUNT_THRESHOLD && (
                    <p className="text-xs text-neutral-500 mt-1 text-right">{remaining} characters remaining</p>
                )}
                {replyError && <p className="text-sm text-red-600 mt-2">{replyError}</p>}

                <div className="flex justify-end mt-3">
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                        disabled={!reply.trim() || isSending}
                        onClick={sendReply}
                    >
                        {isSending && (
                            <span
                                className="inline-block w-4 h-4 mr-2 border-2 border-white border-r-transparent rounded-full animate-spin"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        )}
                        Send Reply
                    </button>
                </div>
            </div>

            <div className="mt-6 text-center">
                <a className="text-blue-600 hover:text-blue-700 font-medium" href={portalUrl(role)}>
                    View full Message Center →
                </a>
            </div>
        </div>
    );
};

export default MessageReply;
